import React, { useState } from 'react';
import { Heart, MessageCircle } from 'lucide-react';
import { mockMemorialAvAs } from '../../services/mockData';
import { useAuth } from '../../contexts/AuthContext';
import { AvALightbox } from '../chat/AvALightbox';

const MemorialAvAsCard = () => {
  const { user } = useAuth();
  const memorialAvAs = mockMemorialAvAs || [];
  const [lightboxMemorial, setLightboxMemorial] = useState(null);

  // Show empty state when no memorial AvAs
  if (!user?.has_memorial_avas || memorialAvAs.length === 0) {
    return (
      <div className="card border-purple-200 bg-purple-50/50" data-testid="memorial-avas-card">
        <div className="card-header">
          <h2 className="card-title text-purple-900">Memorial AvAs</h2>
        </div>
        <div className="text-center py-8">
          <Heart size={32} className="mx-auto text-purple-300 mb-3" />
          <p className="text-sm text-gray-700 font-medium">No memorial AvAs yet</p>
          <p className="text-xs text-gray-600 mt-1.5 max-w-[200px] mx-auto">
            Create a memorial AvA to honour and remember someone special
          </p>
        </div>
      </div>
    );
  }

  const formatAnniversary = (daysUntil) => {
    if (daysUntil === 0) return 'Today';
    if (daysUntil === 1) return 'Tomorrow';
    if (daysUntil < 7) return `In ${daysUntil} days`;
    if (daysUntil < 30) return `In ${Math.floor(daysUntil / 7)} weeks`;
    return `In ${Math.floor(daysUntil / 30)} months`;
  };

  return (
    <div className="card border-purple-200 bg-purple-50" data-testid="memorial-avas-card">
      <div className="card-header">
        <h2 className="card-title text-purple-900">Memorial AvAs</h2>
      </div>

      <div className="space-y-3">
        {memorialAvAs.map((memorial) => (
          <div
            key={memorial.memorial_id}
            className="p-4 rounded-lg bg-white border border-purple-200 shadow-sm"
            data-testid={`memorial-${memorial.memorial_id}`}
          >
            <div className="flex items-start space-x-3 mb-3">
              <button
                type="button"
                onClick={() => setLightboxMemorial(memorial)}
                className="rounded-full focus:outline-none focus:ring-2 focus:ring-purple-400 cursor-zoom-in"
                aria-label={`Enlarge ${memorial.name}`}
                data-testid={`memorial-thumb-${memorial.memorial_id}`}
              >
                <img
                  src={memorial.avatar_url}
                  alt={memorial.name}
                  className="w-12 h-12 rounded-full border-2 border-purple-300"
                />
              </button>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">{memorial.name}</h3>
                <p className="text-sm text-gray-600">{memorial.relationship}</p>
                <p className="text-xs text-purple-600 mt-1">
                  🕯️ Anniversary: {formatAnniversary(memorial.anniversary_days)}
                </p>
              </div>
              <Heart size={20} className="text-purple-500" fill="currentColor" />
            </div>

            <div className="flex items-center justify-between text-xs text-gray-600 mb-3">
              <span>{memorial.conversation_count} conversations</span>
              <span>Last chat: {new Date(memorial.last_conversation).toLocaleDateString()}</span>
            </div>

            <button
              className="w-full py-2 px-3 rounded-lg bg-gradient-to-r from-purple-500 to-purple-600 text-white text-sm font-semibold hover:shadow-lg transition-all"
              data-testid={`connect-memorial-${memorial.memorial_id}`}
            >
              <span className="flex items-center justify-center gap-2">
                <MessageCircle size={16} />
                Connect with {memorial.name}
              </span>
            </button>

            <p className="text-xs text-center text-gray-500 mt-2 italic">
              &quot;Remembering brings comfort and peace.&quot;
            </p>
          </div>
        ))}
      </div>

      {/* Tap-to-enlarge memorial AvA portrait lightbox */}
      <AvALightbox
        open={!!lightboxMemorial}
        onClose={() => setLightboxMemorial(null)}
        name={lightboxMemorial?.name}
        avatarUrl={lightboxMemorial?.avatar_url}
        avaId={lightboxMemorial?.ava_id}
      />
    </div>
  );
};

export default MemorialAvAsCard;
